import { Edit3, Eye, Trash2 } from 'lucide-react';
import { ActionMenu } from '@/components/ui/ActionMenu';
import { CardList } from '@/components/ui/CardList';
import { TablePagination } from '@/components/ui/TablePagination';
import { getRoleBadgeClasses } from '@/utils/role';
import { cn } from '@/utils/cn';
import type { Role } from '../types/rolesList.types';

interface RolesTableBlockProps {
  roles: Role[];
  isLoading: boolean;
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onView: (role: Role) => void;
  onEdit: (role: Role) => void;
  onDelete: (role: Role) => void;
}

export function RolesTableBlock({
  roles,
  isLoading,
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  onView,
  onEdit,
  onDelete,
}: RolesTableBlockProps) {
  const getActions = (role: Role) => [
    { label: 'Xem chi tiết', icon: Eye, onClick: () => onView(role) },
    { label: 'Chỉnh sửa', icon: Edit3, onClick: () => onEdit(role) },
    { label: 'Xóa vai trò', icon: Trash2, destructive: true, disabled: role.isSystem || role.memberCount > 0, onClick: () => onDelete(role) },
  ];

  return (
    <div className="flex min-h-[520px] flex-col space-y-2">
      <div className="hidden flex-1 overflow-hidden rounded-md border border-slate-100 bg-white shadow-sm lg:block">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-slate-100 text-slate-500">
              <th className="p-3 font-normal">Vai trò</th>
              <th className="p-3 font-normal">Loại</th>
              <th className="p-3 font-normal">Thành viên</th>
              <th className="p-3 font-normal">Quyền</th>
              <th className="p-3 font-normal">Rủi ro cao</th>
              <th className="p-3 font-normal">Trạng thái</th>
              <th className="p-3 font-normal"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {isLoading ? (
              Array.from({ length: 6 }).map((_, index) => (
                <tr key={index} className="animate-pulse">
                  <td colSpan={7} className="p-3"><div className="h-10 rounded-md bg-slate-50" /></td>
                </tr>
              ))
            ) : roles.length ? (
              roles.map((role) => (
                <tr key={role.id} className="hover:bg-slate-50">
                  <td className="p-3">
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{role.name}</span>
                        <span className={getRoleBadgeClasses(role.code)}>{role.code}</span>
                      </div>
                      <p className="line-clamp-1 text-slate-500">{role.description || 'Chưa có mô tả'}</p>
                    </div>
                  </td>
                  <td className="p-3">
                    <span className={cn('rounded-md px-2 py-1 text-xs font-semibold', role.isSystem ? 'bg-purple-50 text-purple-700' : 'bg-emerald-50 text-emerald-700')}>
                      {role.isSystem ? 'Hệ thống' : 'Tùy chỉnh'}
                    </span>
                  </td>
                  <td className="p-3 font-semibold text-slate-700">{role.memberCount}</td>
                  <td className="p-3 font-semibold text-slate-700">{role.permissionCount}</td>
                  <td className="p-3 font-semibold text-rose-600">{role.highRiskCount}</td>
                  <td className="p-3">
                    <span className={cn('rounded-md px-2 py-1 text-xs font-semibold', role.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-500')}>
                      {role.status === 'active' ? 'Đang dùng' : 'Tạm ngưng'}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <ActionMenu items={getActions(role)} />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="p-8 text-center text-xs text-slate-400">Không có vai trò phù hợp</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="lg:hidden">
        <CardList
          items={roles}
          isLoading={isLoading}
          emptyMessage="Không có vai trò phù hợp"
          renderCard={(role) => (
            <article key={role.id} className="rounded-md border border-slate-100 bg-white p-3 shadow-sm">
              <div className="space-y-2 text-xs">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 space-y-2">
                    <h3 className="truncate font-medium text-slate-900">{role.name}</h3>
                    <span className={getRoleBadgeClasses(role.code)}>{role.code}</span>
                  </div>
                  <span className={cn('shrink-0 rounded-md px-2 py-1 text-xs font-semibold', role.isSystem ? 'bg-purple-50 text-purple-700' : 'bg-emerald-50 text-emerald-700')}>
                    {role.isSystem ? 'Hệ thống' : 'Tùy chỉnh'}
                  </span>
                </div>
                <p className="line-clamp-2 text-slate-500">{role.description || 'Chưa có mô tả'}</p>
                <div className="grid grid-cols-3 gap-2 border-t border-slate-100 pt-2">
                  <Info label="Thành viên" value={role.memberCount} />
                  <Info label="Quyền" value={role.permissionCount} />
                  <Info label="Rủi ro" value={role.highRiskCount} />
                </div>
                <div className="border-t border-slate-100 pt-2">
                  <ActionMenu items={getActions(role)} />
                </div>
              </div>
            </article>
          )}
        />
      </div>

      <div className="flex-1" />
      <TablePagination
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        itemLabel="vai trò"
      />
    </div>
  );
}

function Info({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md bg-slate-50 p-3 text-center text-xs">
      <p className="font-medium text-slate-900">{value}</p>
      <p className="text-slate-500">{label}</p>
    </div>
  );
}
